<template>
    <v-form class="contract-setup">
        <div class="step-title">{{ $t('get_started.contract_setup_title') }}</div>
        <div class="step-description">
            {{ $t('get_started.contract_setup_description') }}
        </div>

        <v-list class="contracts">
            <v-list-item-group
                v-model="contractIndex"
                mandatory
                color="accent"
            >
                <template v-for="(contract, index) in contracts">
                    <v-list-item
                        :key="index + 'contract'"
                        class="contract"
                    >
                        <v-list-item-icon>
                            <v-icon class="contract-icon">{{ getContractIcon(contract) }}</v-icon>
                        </v-list-item-icon>

                        <v-list-item-content>
                            <div class="d-flex justify-space-between justify-center">
                                <div>
                                    <v-list-item-title>{{ contract.name }}</v-list-item-title>
                                    <v-list-item-subtitle v-if="contract.months_duration > 1">
                                        {{ $t('get_started.contract_duration') }}: {{ contract.months_duration }}
                                    </v-list-item-subtitle>
                                    <v-list-item-subtitle v-else>
                                        {{ $t('get_started.contract_none') }}
                                    </v-list-item-subtitle>
                                </div>

                                <div class="d-flex align-center">
                                    <div v-if="!reseller">
                                        <div class="text-body-2">
                                            <span>{{ Money.parse(contract.price).toFormat() }} / </span>
                                            <span v-if="contract.payment_type === 'monthly'">{{ $t('general.monthly') }}</span>
                                            <span v-else-if="contract.payment_type === 'yearly'">{{ $t('general.yearly') }}</span>
                                        </div>
                                        <div v-if="!noncashOnly" class="text-caption">
                                            {{ $t('general.per_cash_register').toLowerCase() }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </v-list-item-content>
                    </v-list-item>

                    <div
                        v-if="contract.modules && contract.modules.length && contract.number_of_allowed_modules > 0 && contractIndex === index"
                        class="modules"
                        :key="index + 'modules'"
                    >
                        <small>{{ $tc('modules.choose', contract.number_of_allowed_modules) }}</small>
                        <v-checkbox
                            v-for="(mod, modIndex) in contract.modules"
                            :key="modIndex + 'mod'"
                            dense
                            hide-details
                            :input-value="modules.includes(mod.id)"
                            :label="$t(`modules.${mod.name}`)"
                            :disabled="isModuleDisabled(contract, mod)"
                            @click="toggleModule(mod)"
                        />
                    </div>
                </template>
            </v-list-item-group>
        </v-list>

        <div class="footer">
            <v-btn
                class="flex-1"
                color="primary"
                depressed
                :disabled="busy"
                :loading="busy"
                @click="save"
            >
                {{ $t('general.next') }}
            </v-btn>
        </div>
    </v-form>
</template>

<script>
import ApiService from '@/services/ApiService'
import TenantService from '@/services/TenantService'
import * as mutationTypes from '@/store/mutationTypes';

export default {
    created() {
        ApiService.get('/getstarted/contracts').then(response => {
            this.contracts = response.data.contracts
            this.reseller = response.data.reseller
            const contractTag = this.tenant.promo_code === 'FREE' ? 'Promo' : 'Standard'
            let contractIndex = this.contracts.findIndex(c => c.name.includes(contractTag))
            if(contractIndex === -1) {
                contractIndex = this.contracts.findIndex(c => c.name.includes('Start'))
            }
            this.contractIndex = contractIndex === -1
                ? (this.contracts.length ? 0 : null)
                : contractIndex
        })
    },

    data() {
        return {
            busy: false,
            contractIndex: null,
            contracts: [],
            modules: [],
            reseller: null
        };
    },

    watch: {
        contractIndex() {
            this.modules = []

            const contract = this.contracts[this.contractIndex]
            if(this.noncashOnly && locale === 'sr' && contract && contract.number_of_allowed_modules > 0) {
                const einvoicesModule = contract.modules.find(m => m.name === 'einvoices')
                if(einvoicesModule) {
                    this.modules.push(einvoicesModule.id)
                }
            }
        }
    },

    computed: {
        noncashOnly() {
            return this.tenant && this.tenant.venue && !!this.tenant.venue.settings.noncash_only
        }
    },

    methods: {

        /**
         * Save Contract
         */
        save() {
            if(this.contractIndex === null) return

            this.busy = true;

            ApiService.post('/getstarted/contract', {
                contract: this.contracts[this.contractIndex].id,
                module_ids: this.modules
            }).then(response => {
                if(response.data.tenant) {
                    TenantService.setTenant(response.data.tenant);
                }

                this.$store.commit(mutationTypes.SET_MODULES, { modules: response.data.modules || {} });

                this.busy = false;
                this.$emit('finish')
            }).catch(error => {
                this.busy = false;
                this.$snackbar(this.$t('general.action_failed'), 'error');
            })
        },

        getContractIcon(contract) {
            if(contract.name.includes('Seasonal')) {
                return 'fal fa-umbrella-beach'
            }

            if(contract.name.includes('Promo')) {
                return 'fal fa-tags'
            }

            if(contract.name.includes('Start')) {
                return 'fal fa-cash-register'
            }

            if(contract.name.includes('Modul')) {
                return 'fal fa-layer-plus'
            }

            if(contract.name.includes('Premium')) {
                return 'fal fa-rocket'
            }

            return 'fal fa-handshake'
        },

        toggleModule(mod) {
            const modIndex = this.modules.indexOf(mod.id);
            if (modIndex !== -1) {
                this.modules.splice(modIndex, 1);
            } else {
                this.modules.push(mod.id);
            }
        },

        isModuleDisabled(contract, mod) {
            if(this.noncashOnly && locale === 'sr' && mod.name === 'einvoices') return true

            return !this.modules.includes(mod.id) && this.modules.length >= contract.number_of_allowed_modules
        }
    },
};
</script>

<style lang="scss" scoped>
.contract-setup {
    .step-title {
        margin-bottom: 10px;
        font-size: 20px;
    }

    .step-description {
        margin-bottom: 30px;
        font-size: 14px;
        color: #8f9095;
    }

    .footer {
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: flex-start;
    }

    ::v-deep .contracts {
        background: none;

        .contract {
            .contract-icon {
                width: 40px;
            }
        }

        .modules {
            background: #292b38;
            padding: 16px 24px;
        }
    }
}
</style>
